const BASE = '/api/v1';

export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string, public details?: unknown) {
    super(message);
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(BASE + path, {
    method,
    credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : {},
    body: body ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  let json: unknown = null;
  try { json = text ? JSON.parse(text) : null; } catch { /* */ }
  if (!res.ok) {
    const err = (json ?? {}) as { error?: string; message?: string };
    throw new ApiError(res.status, err.error ?? 'UNKNOWN', err.message ?? `HTTP ${res.status}`);
  }
  return json as T;
}

export const api = {
  get: <T = unknown>(path: string) => request<T>('GET', path),
  post: <T = unknown>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T = unknown>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T = unknown>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  delete: <T = unknown>(path: string) => request<T>('DELETE', path),
};

export type Me = {
  user: {
    id: string;
    email: string;
    role: 'super_admin' | 'admin' | 'user';
    totpEnabled: boolean;
    mustChangePassword: boolean;
  } | null;
  mfaVerified: boolean;
};

export type Mailbox = {
  path: string;
  name: string;
  delimiter: string;
  specialUse: string | null;
  flags: string[];
  messages: number;
  unseen: number;
};

export type MessageHeader = {
  uid: number;
  subject: string;
  fromAddress: string;
  fromName: string | null;
  toAddresses: string[];
  date: string;
  flags: string[];
  hasAttachments: boolean;
  preview: string;
  size: number;
};

export type FullMessage = {
  uid: number;
  subject: string;
  fromAddress: string;
  fromName: string | null;
  toAddresses: { address: string; name: string | null }[];
  ccAddresses: { address: string; name: string | null }[];
  date: string;
  flags: string[];
  text: string | null;
  html: string | null;
  attachments: Array<{
    index: number;
    filename: string;
    contentType: string;
    size: number;
  }>;
  size: number;
  messageId: string | null;
  references: string | null;
  inReplyTo: string | null;
};

export type EmailAccount = {
  id: string;
  emailAddress: string;
  displayName: string | null;
  imapHost: string;
  imapPort: number;
  imapSecurity: 'ssl_tls' | 'starttls';
  smtpHost: string;
  smtpPort: number;
  smtpSecurity: 'ssl_tls' | 'starttls';
  username: string;
  isDefault: boolean;
  position: number;
  lastConnectAt: string | null;
  lastError: string | null;
  signature: string | null;
};
